/**
 * Live `paragraph_change` `Y.Map` observe → `applyEmbedPatch` input.
 *
 * `speakerId` / `qac` are in-place field writes. An `id` write is a rename: the lookup id is
 * `keys.get('id').oldValue` when that value is still a paragraph id. A non-string or empty
 * new id is ignored, and the last valid id for this map is kept so a later corrective rename can
 * still find the projected paragraph. Returning a failed patch would make the client full-parse
 * the Yjs map and publish the bad id. Other embed kinds are not live maps.
 */

import * as Y from 'yjs'

import { MARKER_KINDS } from 'src/models/crdtV2'
import type { EmbedFieldPatch } from 'src/models/crdtV2'

const usableParagraphId = (value: unknown): value is string => typeof value === 'string' && value.length > 0

/** Last projected id for a live marker whose Yjs `id` was left malformed. */
const lastValidParagraphId = new WeakMap<Y.Map<unknown>, string>()

/** Build an `applyEmbedPatch` input from an in-place edit of a live `paragraph_change` `Y.Map`. */
export const paragraphChangePatchFromMapEvent = (event: Y.YMapEvent<unknown>): EmbedFieldPatch | null => {
    const target = event.target
    if (!(target instanceof Y.Map) || target.get('kind') !== MARKER_KINDS.paragraphChange) {
        return null
    }

    const idChange = event.keys.get('id')
    const currentId = target.get('id')
    const previousId = usableParagraphId(idChange?.oldValue) ? idChange.oldValue : lastValidParagraphId.get(target)
    let markerId: string
    let renameToId: string | undefined

    if (idChange === undefined) {
        if (usableParagraphId(currentId)) {
            markerId = currentId
            lastValidParagraphId.set(target, currentId)
        } else if (previousId !== undefined) {
            markerId = previousId
        } else {
            return null
        }
    } else if (previousId === undefined) {
        return null
    } else if (usableParagraphId(currentId) && currentId !== previousId) {
        markerId = previousId
        renameToId = currentId
        lastValidParagraphId.set(target, currentId)
    } else {
        markerId = previousId
        lastValidParagraphId.set(target, previousId)
    }

    const fields: EmbedFieldPatch['fields'] = {}
    for (const key of event.keysChanged) {
        if (key === 'speakerId') {
            const value = target.get('speakerId')
            if (typeof value === 'string') {
                fields.speakerId = value
            }
        } else if (key === 'qac') {
            const value = target.get('qac')
            fields.qac = value === 'Q' || value === 'A' ? value : null
        }
    }

    if (renameToId === undefined && Object.keys(fields).length === 0) {
        return null
    }

    return renameToId === undefined ? { markerId, fields } : { markerId, fields, renameToId }
}
